/**
 * savedListLink.ts — #1877. The durable link from a chat message to the to-do
 * list that message's save created.
 *
 * Two halves that must agree on one string, which is why they share a module:
 * ChatMessages WRITES the sessionStorage key when she taps a saved pill, and
 * WheneverLists READS it on mount to decide which list to expand. Two copies of
 * `"kin_open_todo_list"` would be a silent no-op the first time one of them was
 * renamed — the navigation would still happen, just land on nothing expanded,
 * which looks like the feature half-working rather than like a bug.
 */

/**
 * sessionStorage key carrying "expand THIS list when Tasks → Whenever mounts".
 *
 * ⚠️ sessionStorage rather than a `?open=` URL param, deliberately: `TodayTab`
 * strips `open` unconditionally and hand-maintains which tab owns which value,
 * so a new param means editing the most fragile file in the repo. The sibling
 * key `kin_rtf_segment` already drives this exact journey.
 */
export const OPEN_TODO_LIST_KEY = "kin_open_todo_list";

/**
 * Read the pending "expand this list" id and CLEAR it in the same breath.
 *
 * ⚠️ The clear is unconditional — it happens even when the id matches no list
 * she still has. A key left behind would re-expand on her next unrelated visit
 * to Tasks, which reads as the app remembering something she did not ask it to.
 * "Take", not "get", for that reason: calling it twice gives null the second
 * time, and that is the contract.
 *
 * Returns null in private mode or when storage throws — never throws itself,
 * because failing to expand a list must not take the Whenever view down with it.
 */
export function takeOpenTodoListId(): string | null {
  try {
    const id = sessionStorage.getItem(OPEN_TODO_LIST_KEY);
    sessionStorage.removeItem(OPEN_TODO_LIST_KEY);
    return id && id.trim() ? id : null;
  } catch {
    return null;
  }
}

/** The one query this module runs, structurally, so a test can fake it. */
type MessageUpdater = {
  from: (table: string) => {
    update: (patch: { saved_list_id: string }) => {
      eq: (
        column: string,
        value: string,
      ) => {
        select: (
          columns: string,
        ) => PromiseLike<{ data: { id: string }[] | null; error: unknown | null }>;
      };
    };
  };
};

/**
 * Record on the message row which list its save landed in.
 *
 * ⚠️ RETURNS WHETHER THE LINK IS REAL, and the caller must not promise one
 * unless it is. House rule 16: an RLS-filtered UPDATE matching zero rows comes
 * back 200 with `[]` and no error, so `!error` alone would confirm a link that
 * does not exist. The `.select("id")` plus the length check is what makes this
 * answerable.
 *
 * ⚠️ A FALSE RETURN IS NOT A FAILED SAVE. By the time this runs, the items are
 * already on her list — that write was verified separately. This is the
 * *bookmark*, and losing it costs her a tappable pill, not her to-dos. So every
 * failure path returns false quietly rather than throwing: a thrown error here
 * would turn a successful save into an error toast, which is the one outcome
 * worse than a missing link.
 *
 * That also covers the pre-migration window for free. Code deploys on merge and
 * the SQL is run by hand afterwards, so `saved_list_id` may not exist yet; the
 * update answers 42703, this returns false, and the save reports itself
 * truthfully with no link offered.
 */
export async function recordSavedList(
  client: MessageUpdater,
  messageId: string | null | undefined,
  listId: string,
): Promise<boolean> {
  // No message id — nothing to write it on. Not an error: a message rendered
  // before #1882's mint, or one whose insert was never proved, legitimately has
  // none, and the save itself is unaffected.
  if (!messageId) return false;
  try {
    const { data, error } = await client
      .from("messages")
      .update({ saved_list_id: listId })
      .eq("id", messageId)
      .select("id");
    if (error || !data?.length) {
      console.warn("[SaveTodosButton] could not record saved_list_id; save stands", error);
      return false;
    }
    return true;
  } catch (e) {
    console.warn("[SaveTodosButton] recording saved_list_id threw; save stands", e);
    return false;
  }
}
